import React, { useEffect, useState } from 'react';

export interface EdgeTypeChoice {
  category: 'procedural' | 'structural';
  subtype: string;
  label: string;
}

interface EdgeTypeDialogProps {
  visible: boolean;
  sourceLabel: string;
  targetLabel: string;
  sourceNodeType: string;
  isProcessSubGraph?: boolean;
  onConfirm: (choice: EdgeTypeChoice) => void;
  onCancel: () => void;
}

// 仅展示以下 3 种类型（与截图一致）
const STRUCTURAL_TYPES: EdgeTypeChoice[] = [
  { category: 'structural', subtype: 'exhibition-characterization', label: '展示关系' },
];

const PROCEDURAL_TYPES: EdgeTypeChoice[] = [
  { category: 'procedural', subtype: 'generation', label: '生成' },
  { category: 'procedural', subtype: 'effect', label: '影响' },
  { category: 'procedural', subtype: 'agent-enablement', label: 'Agent 使能' },
];

const INVOCATION_TYPE: EdgeTypeChoice = {
  category: 'procedural',
  subtype: 'invocation',
  label: '调用',
};

// SVG 图标：三角形嵌套三角形（展示关系）- 右侧箭头
const ExhibitionIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <path d="M24 4 L40 30 L8 30 Z" fill="none" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M24 14 L32 28 L16 28 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

// SVG 图标：菱形箭头 →（生成）- 目标端空心箭头
const GenerationIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="30" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M30 10 L42 18 L30 26 L34 18 Z" fill="#ffffff" stroke="#5a7fa5" strokeWidth="2" />
    <circle cx="4" cy="18" r="3" fill="#5a7fa5" />
  </svg>
);

// SVG 图标：粗箭头 →（影响）- 目标端实心箭头
const EffectIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="28" y2="18" stroke="#5a7fa5" strokeWidth="2.5" />
    <path d="M26 8 L44 18 L26 28 L32 18 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

// SVG 图标：实心圆点 + ¬C 标识（Agent 使能关系）
const AgentEnablementIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="4" y1="18" x2="36" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <circle cx="40" cy="18" r="5" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
    <text x="30" y="10" fontSize="8" fontWeight="bold" fill="#5a7fa5" textAnchor="middle">¬C</text>
  </svg>
);

// SVG 图标：调用（过程间调用）
const InvocationIcon: React.FC = () => (
  <svg width="48" height="36" viewBox="0 0 48 36">
    <line x1="6" y1="18" x2="32" y2="18" stroke="#5a7fa5" strokeWidth="2" />
    <path d="M30 10 L42 18 L30 26 L34 18 Z" fill="#5a7fa5" stroke="#5a7fa5" strokeWidth="1.5" />
  </svg>
);

const EdgeTypeDialog: React.FC<EdgeTypeDialogProps> = ({
  visible,
  sourceLabel,
  targetLabel,
  sourceNodeType,
  isProcessSubGraph,
  onConfirm,
  onCancel,
}) => {
  const [selectedType, setSelectedType] = useState<EdgeTypeChoice | null>(null);

  const structuralTypes = isProcessSubGraph ? [] : STRUCTURAL_TYPES;
  const proceduralTypes = isProcessSubGraph ? [INVOCATION_TYPE] : PROCEDURAL_TYPES;

  useEffect(() => {
    if (!visible) {
      setSelectedType(null);
      return;
    }
    if (isProcessSubGraph) {
      setSelectedType(INVOCATION_TYPE);
    }
  }, [visible, isProcessSubGraph]);

  if (!visible) return null;

  const handleConfirm = () => {
    if (selectedType) {
      onConfirm(selectedType);
      setSelectedType(null);
    }
  };

  const handleCancel = () => {
    setSelectedType(null);
    onCancel();
  };

  const renderOption = (type: EdgeTypeChoice) => {
    const isSelected = selectedType === type;
    const IconComponent =
      type.subtype === 'exhibition-characterization'
        ? ExhibitionIcon
        : type.subtype === 'generation'
          ? GenerationIcon
          : type.subtype === 'agent-enablement'
            ? AgentEnablementIcon
            : type.subtype === 'invocation'
              ? InvocationIcon
              : EffectIcon;

    return (
      <button
        key={type.subtype}
        onClick={() => setSelectedType(type)}
        style={{
          padding: '12px 20px',
          border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
          borderRadius: 6,
          background: isSelected ? '#e6f7ff' : '#fff',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          minWidth: 100,
          transition: 'all 0.2s',
        }}
      >
        <IconComponent />
        <span style={{ fontSize: 12, color: '#333' }}>{type.label}</span>
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 24,
          minWidth: 480,
          maxWidth: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>创建连线</h3>
          <button
            onClick={handleCancel}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 20,
              cursor: 'pointer',
              color: '#999',
              padding: '0 4px',
            }}
          >
            ×
          </button>
        </div>

        {/* Direction */}
        <div style={{ marginBottom: 20, fontSize: 13, color: '#666' }}>
          连线方向：<span style={{ color: '#1890ff' }}>{sourceLabel}</span>
          <span style={{ margin: '0 8px', color: '#999' }}>→</span>
          <span style={{ color: '#52c41a' }}>{targetLabel}</span>
        </div>

        {structuralTypes.length > 0 && (
          <div style={{ marginBottom: 24 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#333',
                marginBottom: 12,
              }}
            >
              结构关系：
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {structuralTypes.map(renderOption)}
            </div>
          </div>
        )}

        {proceduralTypes.length > 0 && (
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#333',
                marginBottom: 12,
              }}
            >
              过程关系：
            </div>
            <div
              style={{
                display: 'flex',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              {proceduralTypes.map(renderOption)}
            </div>
          </div>
        )}

        {/* Footer buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 24,
          }}
        >
          <button
            onClick={handleCancel}
            style={{
              padding: '6px 16px',
              border: '1px solid #d9d9d9',
              borderRadius: 4,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            取消
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedType}
            style={{
              padding: '6px 16px',
              border: 'none',
              borderRadius: 4,
              background: selectedType ? '#1890ff' : '#d9d9d9',
              color: '#fff',
              cursor: selectedType ? 'pointer' : 'not-allowed',
              fontSize: 13,
            }}
          >
            创建
          </button>
        </div>
      </div>
    </div>
  );
};

export default EdgeTypeDialog;
